import { useInfiniteQuery, QueryKey, InfiniteData } from '@tanstack/react-query'
import { useMemo } from 'react'

import { PaginationMeta } from '@api/orval/apiHooks'
export interface InfinitePage<T> {
  collection?: T[]
  meta?: Partial<PaginationMeta>
}
interface Options<T, Q> {
  fetchPage: (p: Q) => Promise<InfinitePage<T>>
  query: Q
  queryKey: QueryKey
  refetchInterval?: number | false
}
/**
 * Replaces useInfiniteTableData with TanStack Query
 */
export const useInfiniteTableData = <T, Q>({ fetchPage, query, queryKey, refetchInterval }: Options<T, Q>) => {
  // Include query in key to refetch when query changes

  const { data, error, isFetching, fetchNextPage, hasNextPage, refetch } = useInfiniteQuery<
    InfinitePage<T>,
    Error,
    InfinitePage<T>,
    QueryKey
  >({
    queryKey: [...queryKey, query] as QueryKey,
    queryFn: async ({ pageParam }) => {
      const result = await fetchPage({ ...query, after: pageParam })

      return {
        collection: result.collection ?? [],
        meta: result.meta ?? { has_next: false, next_cursor: null, orderable_fields: [] },
      }
    },
    getNextPageParam: (lastPage) => lastPage.meta?.next_cursor ?? undefined,
    initialPageParam: undefined,
    refetchInterval,
  })

  const infiniteData = data as InfiniteData<InfinitePage<T>> | undefined
  const items = useMemo(() => infiniteData?.pages.flatMap((p) => p.collection ?? []) ?? [], [infiniteData])
  const meta = useMemo(
    () =>
      infiniteData?.pages[infiniteData.pages.length - 1]?.meta ?? {
        has_next: false,
        next_cursor: null,
        orderable_fields: [],
      },
    [infiniteData],
  )
  const fetchNext = () => {
    if (hasNextPage && !isFetching) fetchNextPage()
  }

  return { items, meta, error, isLoading: isFetching, fetchNext, refetch }
}
